import type { LeaveType } from "../types";

type Props = {
  items: LeaveType[];
  onEdit: (item: LeaveType) => void;
  onToggleActive: (item: LeaveType) => void;
};

export function LeaveTypesTable({ items, onEdit, onToggleActive }: Props) {
  return (
    <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Gender</th>
              <th className="px-4 py-3">Default Entitlement</th>
              <th className="px-4 py-3">Max / Request</th>
              <th className="px-4 py-3">Counting</th>
              <th className="px-4 py-3">Rules</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Action</th>
            </tr>
          </thead>

          <tbody className="divide-y">
            {items.map((item) => (
              <tr key={item.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-black">{item.code}</td>

                <td className="px-4 py-3">
                  <div className="font-bold text-slate-900">{item.name}</div>
                  <div className="text-xs text-slate-500">
                    {item.description || "-"}
                  </div>
                </td>

                <td className="px-4 py-3">
                  <GenderBadge value={item.applicableGender} />
                </td>

                <td className="px-4 py-3">
                  {item.requiresBalance ? item.annualEntitlementDays ?? 0 : "-"}
                </td>

                <td className="px-4 py-3">{item.maxDaysPerRequest ?? "-"}</td>

                <td className="px-4 py-3 text-xs text-slate-600">
                  <div>{formatValue(item.dayCountMode || "WORKING_DAYS")}</div>
                  <div>
                    Weekends: {item.excludeWeekends ? "Excluded" : "Counted"}
                  </div>
                  <div>
                    Holidays:{" "}
                    {item.excludePublicHolidays ? "Excluded" : "Counted"}
                  </div>
                </td>

                <td className="px-4 py-3 text-xs text-slate-500">
                  <div>Paid: {item.paid ? "Yes" : "No"}</div>
                  <div>Balance: {item.requiresBalance ? "Yes" : "No"}</div>
                  <div>Attachment: {item.requiresAttachment ? "Yes" : "No"}</div>
                  <div>
                    Relief: {item.requiresReliefEmployee ? "Required" : "No"}
                  </div>
                  <div>
                    Notice:{" "}
                    {item.minimumNoticeDays !== null &&
                    item.minimumNoticeDays !== undefined
                      ? `${item.minimumNoticeDays} day(s)`
                      : "-"}
                  </div>
                  <div>
                    Workflow:{" "}
                    {item.workflowName ? (
                      <span className="font-bold text-indigo-700">
                        {item.workflowName}
                      </span>
                    ) : (
                      "Default rules"
                    )}
                  </div>
                </td>

                <td className="px-4 py-3">
                  <Status active={item.active} />
                </td>

                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => onEdit(item)}
                      className="rounded-xl border px-3 py-1.5 text-xs font-bold hover:bg-slate-50"
                    >
                      Edit
                    </button>

                    <button
                      type="button"
                      onClick={() => onToggleActive(item)}
                      className="rounded-xl border px-3 py-1.5 text-xs font-bold hover:bg-slate-50"
                    >
                      {item.active ? "Deactivate" : "Activate"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}

            {!items.length ? (
              <tr>
                <td
                  colSpan={9}
                  className="px-4 py-10 text-center text-slate-500"
                >
                  No leave types configured.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Status({ active }: { active: boolean }) {
  return (
    <span
      className={`rounded-full px-3 py-1 text-xs font-bold ${
        active
          ? "bg-emerald-100 text-emerald-700"
          : "bg-slate-200 text-slate-700"
      }`}
    >
      {active ? "Active" : "Inactive"}
    </span>
  );
}

function GenderBadge({ value }: { value?: string | null }) {
  const normalized = value || "ALL";

  const classes: Record<string, string> = {
    ALL: "bg-slate-100 text-slate-700",
    MALE: "bg-blue-100 text-blue-700",
    FEMALE: "bg-pink-100 text-pink-700",
  };

  return (
    <span
      className={`rounded-full px-3 py-1 text-xs font-bold ${
        classes[normalized] || classes.ALL
      }`}
    >
      {formatGender(normalized)}
    </span>
  );
}

function formatGender(value?: string | null) {
  if (!value || value === "ALL") return "All";
  if (value === "MALE") return "Male";
  if (value === "FEMALE") return "Female";
  return formatValue(value);
}

function formatValue(value?: string | null) {
  if (!value) return "-";

  return value
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}